import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, MapPin, AlertTriangle, CheckCircle, Clock } from "lucide-react";

const TIER_STYLES = {
  A: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", label: "Direct Download" },
  B: { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-700", label: "Web Scrape" },
  C: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", label: "Needs Human Input" },
  D: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-500", label: "Paper / Fallback" },
};

export default function CityClassificationPanel() {
  const [summary, setSummary] = useState(null);
  const [humanQueue, setHumanQueue] = useState([]);
  const [tierDCities, setTierDCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reclassifying, setReclassifying] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [sumRes, hqRes, tdRes] = await Promise.all([
        axios.get(`${API}/admin/city-classifications/summary`),
        axios.get(`${API}/admin/city-classifications/human-queue`),
        axios.get(`${API}/admin/city-classifications/tier-d`),
      ]);
      setSummary(sumRes.data);
      setHumanQueue(hqRes.data.cities || []);
      setTierDCities(tdRes.data.cities || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const reclassify = async (city, state) => {
    setReclassifying(city);
    try {
      const res = await axios.post(`${API}/admin/city-classifications/reclassify`, { city, state });
      toast.success(`${city} reclassified to Tier ${res.data.tier}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reclassification failed");
    } finally { setReclassifying(null); }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="city-classification-panel">
      {/* Tier distribution */}
      {summary && (
        <div className="grid grid-cols-4 gap-2" data-testid="tier-distribution">
          {["A", "B", "C", "D"].map(tier => {
            const s = TIER_STYLES[tier];
            return (
              <div key={tier} className={`${s.bg} rounded-lg p-2.5 text-center border ${s.border}`}>
                <p className={`text-lg font-bold ${s.text}`} data-testid={`tier-${tier}-count`}>{summary[tier] || 0}</p>
                <p className="text-[10px] text-slate-500">Tier {tier}</p>
                <p className="text-[9px] text-slate-400">{s.label}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Human input queue (Tier C) */}
      {humanQueue.length > 0 && (
        <div data-testid="human-input-queue">
          <p className="text-xs font-semibold text-amber-700 mb-2 flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5" /> Needs Human Input ({humanQueue.length})
          </p>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {humanQueue.map((c) => (
              <div key={`${c.city}-${c.state}`} className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs"
                   data-testid={`human-queue-${c.city}`}>
                <MapPin className="w-3 h-3 text-amber-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-slate-700">{c.city}, {c.state}</span>
                  {c.human_input_notes && <p className="text-[10px] text-amber-600 truncate">{c.human_input_notes}</p>}
                </div>
                <button onClick={() => reclassify(c.city, c.state)} disabled={reclassifying === c.city}
                  className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-[10px] font-semibold hover:bg-amber-200 transition flex-shrink-0"
                  data-testid={`reclassify-btn-${c.city}`}>
                  {reclassifying === c.city ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tier D cities with reclassify dates */}
      {tierDCities.length > 0 && (
        <div data-testid="tier-d-list">
          <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" /> Tier D — Scheduled Revisits ({tierDCities.length})
          </p>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {tierDCities.map((c) => (
              <div key={`${c.city}-${c.state}`} className="flex items-center gap-2 p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs"
                   data-testid={`tier-d-${c.city}`}>
                <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0" />
                <span className="font-medium text-slate-600 flex-1">{c.city}, {c.state}</span>
                <span className="text-[10px] text-slate-400">{c.reclassify_after ? `revisit ${c.reclassify_after.slice(0,10)}` : "no date"}</span>
                <button onClick={() => reclassify(c.city, c.state)} disabled={reclassifying === c.city}
                  className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-[10px] font-semibold hover:bg-slate-200 transition flex-shrink-0"
                  data-testid={`reclassify-now-${c.city}`}>
                  {reclassifying === c.city ? <Loader2 className="w-3 h-3 animate-spin" /> : "Reclassify"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {!humanQueue.length && !tierDCities.length && summary?.total === 0 && (
        <p className="text-xs text-slate-400 text-center py-4">No city classifications yet. Cities are classified when Sweep 0 runs.</p>
      )}
    </div>
  );
}
